import React, { useState, useEffect } from 'react';
import { Cherry, Plus, Trash2 } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow,
    AIButton, AIUndoButton, PollResults,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';
import { useConfirm } from '../../store/ConfirmDialogContext';

const DEFAULT_OPTIONS = ['You win!', 'Better luck next time', '10% off'];
// allowUpdateResponse is intentionally always false and not exposed as a setting here —
// a slot machine is a one-time spin, not an editable answer (same rule as Scratch Card).
const DEFAULT_SETTINGS = { pollResults: 'hidden', isLocked: false, allowUpdateResponse: false, description: false, descText: '' };

const DUMMY_DATA = {
    question: 'Spin the reels for a chance to win! 🎰',
    options: ['Win a free coffee ☕', 'Try again next time', '10% off your next order', 'Win a mystery gift 🎁'],
    settings: { ...DEFAULT_SETTINGS, description: true, descText: 'Everyone gets one spin — good luck!' },
};

const SlotMachine = ({ event, activity, isLaunched, onLaunch, onStop, onNext, onPrev, hasNext, hasPrev, onSettingsUpdated }) => {
    const confirm = useConfirm();
    const [question, setQuestion] = useState('');
    const [editingQuestion, setEditingQuestion] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [options, setOptions] = useState(DEFAULT_OPTIONS);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [isImproving, setIsImproving] = useState(false);
    const [isImprovingDesc, setIsImprovingDesc] = useState(false);
    const [previousQuestion, setPreviousQuestion] = useState(null);
    const [previousDescText, setPreviousDescText] = useState(null);

    const { content, isLoading, isSaving, saveStatus, saveContent } = useActivityContent(event?.id, activity?.id);

    useEffect(() => {
        if (!content) return;
        setQuestion(content.question || '');
        if (Array.isArray(content.options) && content.options.length) setOptions(content.options);
        if (content.settings) setSettings(s => ({ ...s, ...content.settings, allowUpdateResponse: false }));
    }, [content]);

    const buildContent = () => ({ question, options, settings: { ...settings, allowUpdateResponse: false } });
    const validate = () => {
        if (!question.trim()) return 'Please enter a question.';
        const nonEmpty = options.filter(o => o.trim());
        if (nonEmpty.length < 2) return 'At least 2 possible outcomes are required.';
        return null;
    };
    const updateSetting = (key, val) => setSettings(s => ({ ...s, [key]: val }));

    const handleImproveWithAI = async () => {
        if (!question.trim()) return;
        setIsImproving(true);
        try {
            const res = await request(`${API_PATH}/ai/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'improve', text: question }) });
            if (res.ok) { const data = await res.json(); if (data.suggestion) { setPreviousQuestion(question); setQuestion(data.suggestion); } } else { alert('Failed to improve with AI. Please try again.'); }
        } catch (err) { console.error(err); alert('Failed to improve with AI. Please try again.'); } finally { setIsImproving(false); }
    };

    const handleImproveDescWithAI = async () => {
        const text = settings.descText?.trim() || '';
        if (!text) return;
        setIsImprovingDesc(true);
        try {
            const res = await request(`${API_PATH}/ai/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'improve', text }) });
            if (res.ok) { const data = await res.json(); if (data.suggestion) { setPreviousDescText(settings.descText); updateSetting('descText', data.suggestion); } } else { alert('Failed to improve with AI. Please try again.'); }
        } catch (err) { console.error(err); alert('Failed to improve with AI. Please try again.'); } finally { setIsImprovingDesc(false); }
    };

    const handleUndoQuestion = () => { if (previousQuestion !== null) { setQuestion(previousQuestion); setPreviousQuestion(null); } };
    const handleUndoDesc = () => { if (previousDescText !== null) { updateSetting('descText', previousDescText); setPreviousDescText(null); } };

    const loadDummy = () => {
        setQuestion(DUMMY_DATA.question);
        setOptions(DUMMY_DATA.options);
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const addOption = () => setOptions(prev => [...prev, '']);
    const updateOption = (i, v) => setOptions(prev => prev.map((o, j) => (j === i ? v : o)));
    const removeOption = async (i) => {
        const responseCount = activity?.response_count || 0;
        const ok = await confirm({
            title: 'Delete Outcome',
            message: 'Delete this outcome?',
            details: responseCount > 0 ? (
                <>
                    <span style={{ fontWeight: 600 }}>⚠️ These reels have already been spun {responseCount} time{responseCount !== 1 ? 's' : ''}.</span>
                    <br />
                    Anyone who already won this outcome will no longer see it in the results breakdown.
                </>
            ) : undefined,
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setOptions(prev => prev.filter((_, j) => j !== i));
    };

    const participantPreview = (
        <ParticipantExperience step="activity" name="Preview" event={event} activity={{ type: 'slot-machine', title: question || 'Slot Machine', content: buildContent() }} isPreview />
    );

    const left = (
        <>
            <div className={styles.questionCard}>
                <div className={styles.questionContent}>
                    <div className={styles.questionLabelRow}>
                        <span className={styles.questionLabel}>Question <span className={styles.requiredStar}>*</span></span>
                        <span className={styles.aiActions}>
                            <AIButton onClick={handleImproveWithAI} loading={isImproving} />
                            {previousQuestion !== null && <AIUndoButton onClick={handleUndoQuestion} label="Undo" />}
                        </span>
                    </div>
                    {editingQuestion ? (
                        <input className={styles.questionInput} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="e.g. Spin the reels for a chance to win!" autoFocus onBlur={() => setEditingQuestion(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setEditingQuestion(false); } }} />
                    ) : (
                        <Tooltip title="Click to edit question" placement="top">
                            <div className={`${styles.questionTextDisplay} ${!question ? styles.questionTextPlaceholder : ''}`} role="button" tabIndex={0} onClick={() => setEditingQuestion(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingQuestion(true); } }}>
                                {question || 'Type your question here...'}
                            </div>
                        </Tooltip>
                    )}

                    {settings.description && (
                        <div className={styles.questionDescription}>
                            <div className={styles.questionLabelRow}>
                                <span className={styles.descLabel}>Description</span>
                                <span className={styles.aiActions}>
                                    <AIButton onClick={handleImproveDescWithAI} loading={isImprovingDesc} />
                                    {previousDescText !== null && <AIUndoButton onClick={handleUndoDesc} label="Undo" />}
                                </span>
                            </div>
                            {editingDesc ? (
                                <input className={styles.descInput} value={settings.descText || ''} onChange={(e) => updateSetting('descText', e.target.value)} placeholder="Type description here..." autoFocus onBlur={() => setEditingDesc(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setEditingDesc(false); } }} />
                            ) : (
                                <Tooltip title="Click to edit description" placement="top">
                                    <div className={`${styles.descTextDisplay} ${!settings.descText ? styles.descTextPlaceholder : ''}`} role="button" tabIndex={0} onClick={() => setEditingDesc(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingDesc(true); } }}>
                                        {settings.descText || 'Type description here...'}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    )}

                    <div className={styles.optionsCard}>
                        <div className={styles.optionsHeader}>
                            <span className={styles.optionsLabel}>Possible outcomes</span>
                            <span className={styles.markCorrectHint}>Equal odds — one is drawn at random per participant</span>
                        </div>

                        {options.map((opt, i) => (
                            <div key={i} className={styles.optionRow}>
                                <span className={styles.optionLetter}>{i + 1}</span>
                                <input
                                    className={styles.optionInput}
                                    value={opt}
                                    onChange={(e) => updateOption(i, e.target.value)}
                                    placeholder={`Outcome ${i + 1} (e.g. "You win!")`}
                                />
                                {options.length > 2 && (
                                    <button className={styles.optionRemoveBtn} onClick={() => removeOption(i)} aria-label="Remove outcome">
                                        <Trash2 size={13} />
                                    </button>
                                )}
                            </div>
                        ))}

                        <div className={styles.optionsFooter}>
                            <button className={styles.addOptionBtn} onClick={addOption}>
                                <Plus size={14} /> Add outcome
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    const right = (
        <SettingsGroup title="Settings">
            <SettingsRow label="Poll description" description="Add description visible to participants">
                <Toggle checked={settings.description} onChange={(e) => updateSetting('description', e.target.checked)} />
            </SettingsRow>
            <PollResults value={settings.pollResults} onChange={v => updateSetting('pollResults', v)} />
        </SettingsGroup>
    );

    return (
        <ActivityLayout
            icon={<Cherry size={20} />} color="var(--cat-orange)"
            title="Slot Machine" eventName={event?.name}
            left={left} right={right} participantPreview={participantPreview}
            onSave={() => saveContent(buildContent())}
            onLoadExample={loadDummy}
            isSaving={isSaving} saveStatus={saveStatus} isLoading={isLoading}
            validate={validate} event={event} activity={activity} isLaunched={isLaunched}
            onLaunch={onLaunch} onStop={onStop} onNext={onNext} onPrev={onPrev}
            hasNext={hasNext} hasPrev={hasPrev} onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default SlotMachine;
